import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk, RootState} from "../../app/store";
import {ListViewModel,
        ListViewFactory,
        ListViewFilterModel,
        ListViewFilterJSON,
        ListViewFilterFactory,
        ObjectModel,
        ObjectFactory,
        ObjectPageModel,
        ObjectPageFactory,
        RecordModel,
        RecordFactory} from "../../models/models";
import {PlotlyChartFigure} from "../../models/modelsDashboard";
import {SortOrderModel,
        SortOrderFactory,
        BarChartUIPreferenceJSON,
        BarChartUIPreferenceModel,
        BarChartUIPreferenceFactory} from '../../models/modelsUserConfiguration';
import {ListConfigurationModel,
        ListConfigurationJSON,
        ListConfigurationFactory,
        ListViewConfigurationModel,
        ListViewConfigurationJSON,
        ListViewConfigurationFactory,
        PaginatorFactory,
        PaginatorModel,
        SearchTermFactory,
        SearchTermModel} from  '../../models/modelsListConfiguration';
import Config from "../../config";


interface ObjectState {
  isFetching: boolean;
  selectedListView: ListViewModel | null;
  listViews: ListViewModel[];
  records: RecordModel[];
  objectMetadata: ObjectModel | null,
  objectPage: ObjectPageModel | null,
  listConfig: ListConfigurationModel,
  listViewConfig: ListViewConfigurationModel,
  chart: PlotlyChartFigure | null,
  error: string | null
}

const initialState: ObjectState = {
  isFetching: false,
  records: [],
  objectMetadata: null,
  listViews: [],
  selectedListView: null,
  objectPage: null,
  listConfig: new ListConfigurationFactory().init(),
  listViewConfig: new ListViewConfigurationFactory().init(),
  chart: null,
  error: null
};


const sortFunction = (recordA: RecordModel,
                      recordB: RecordModel,
                      sortOrder: SortOrderModel) => {

  const fieldApiName = sortOrder.fieldApiName
  const sortDirection = sortOrder.sortDirection

  const fieldA = recordA.fields[fieldApiName]//recordA.fields.find((thisField) => thisField.fieldName === fieldApiName);
  const fieldB = recordB.fields[fieldApiName]//recordB.fields.find((thisField) => thisField.fieldName === fieldApiName);

  let sortIndex = 1

  if(sortDirection === 'asc'){
    fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=1 : sortIndex=-1
  }
  if(sortDirection === 'desc'){
    fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=-1 : sortIndex=1
  }

  return sortIndex

}


export const objectSlice = createSlice({
  name: "object",
  initialState,
  reducers: {
    storeFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    storeSelectedListView: (state, action: PayloadAction<ListViewModel | null>) => {
      state.selectedListView = action.payload;
    },
    storeListViews: (state, action: PayloadAction<ListViewModel[]>) => {
      state.listViews = action.payload.map((record) => record);
    },
    storeRecords: (state, action: PayloadAction<RecordModel[]>) => {
      const newRecords = action.payload.map((record) => record);
      let allRecords = state.records.map((record) => record)
      allRecords = allRecords.concat(newRecords)
      state.records = allRecords.map((record) => record)
    },
    restoreRecords: (state, action: PayloadAction<RecordModel[]>) => {
      state.records = action.payload.map((record) => record)
    },
    storeObjectPage: (state, action: PayloadAction<ObjectPageModel | null>) => {
      state.objectPage = action.payload;
    },
    storeListViewConfig: (state, action: PayloadAction<ListViewConfigurationModel>) => {
      const loadedPages = [...state.listViewConfig.paginator.loadedPages, action.payload.paginator.currentPage];
      state.listViewConfig = action.payload;
      state.listViewConfig.paginator.loadedPages=loadedPages;
    },
    storePageLoaded: (state, action: PayloadAction<number>) => {
      //store when a page is loaded to loadedPages
      //and remove the page from loadingPages
      const pageNumber = action.payload;
      const loadedPages = [...state.listViewConfig.paginator.loadedPages, pageNumber];
      const loadingPages = state.listViewConfig.paginator.loadingPages.filter((thisPage) => thisPage !== pageNumber)
      state.listViewConfig.paginator.loadedPages=loadedPages;
      state.listViewConfig.paginator.loadingPages=loadingPages;
    },
    storePageLoading: (state, action: PayloadAction<number>) => {
      //store when a page is loading
      const pageNumber = action.payload;
      const loadingPages = [...state.listViewConfig.paginator.loadingPages, pageNumber];
      state.listViewConfig.paginator.loadingPages=loadingPages;
    },
    removePageLoading: (state, action: PayloadAction<number>) => {
      //remove the page from loadingPages
      const pageNumber = action.payload;
      const loadingPages = state.listViewConfig.paginator.loadingPages.filter((thisPage) => thisPage !== pageNumber)
      state.listViewConfig.paginator.loadingPages=loadingPages;
    },
    storeListConfig: (state, action: PayloadAction<ListConfigurationModel>) => {
      state.listConfig = action.payload;
    },
    storeObjectMetadata: (state, action: PayloadAction<ObjectModel | null>) => {
      state.objectMetadata = action.payload;
    },
    storeChart: (state, action: PayloadAction<PlotlyChartFigure | null>) => {
      state.chart = action.payload;
    },
    storeError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    purgePaginator: (state) => {
      state.listViewConfig.paginator = new PaginatorFactory().init();
    },
    purgeStore: (state) => {
      state.records = [];
      state.objectPage = null;
//      state.objectMetadata = null;
      state.selectedListView = null;
      state.listViews = [];
      state.listConfig = new ListConfigurationFactory().init();
      state.listViewConfig = new ListViewConfigurationFactory().init();
      state.chart = null;
      state.isFetching = false;
      state.error = null;
    },
  },
});



export const { storeListViews,
               storeSelectedListView,
               storeRecords,
               restoreRecords,
               storeObjectPage,
               storeListConfig,
               storeListViewConfig,
               storePageLoaded,
               storePageLoading,
               removePageLoading,
               storeObjectMetadata,
               storeChart,
               storeFetching,
               storeError,
               purgeStore,
               purgePaginator } = objectSlice.actions;


interface RequestBody {
  crm_barchart_config: BarChartUIPreferenceJSON[] | null,
  crm_list_config: ListConfigurationJSON | null,
  crm_listview_config: ListViewConfigurationJSON | null
}

export const retrieveObjectInformation =
  (objectApiName: string | null,
   listViewApiName: string | undefined | null,
   listConfig: ListConfigurationModel | null,
   listViewConfig: ListViewConfigurationModel | null,
   barChartConfig: BarChartUIPreferenceModel[] | null,
   accessToken: string,
   refetch: boolean,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //load listviews, selected list view, records and object metadata
    //for a given objectApiName
    //if refetch is true, object metadata state is not being altered
    //listViewDisplaySetting: can switch between "List", "BarChart", etc.

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    const requestBody: RequestBody = {
      crm_barchart_config: null,
      crm_list_config: null,
      crm_listview_config: null
    };

    if(barChartConfig) {
      const barChartFactory = new BarChartUIPreferenceFactory();
      const barChartConfigJSON = barChartConfig.map((thisBarChartGroup: BarChartUIPreferenceModel) => barChartFactory.toJSON(thisBarChartGroup));
      requestBody.crm_barchart_config = barChartConfigJSON
    }

    if(listConfig) {
      const listConfigJSON = new ListConfigurationFactory().toJSON(listConfig)
      requestBody.crm_list_config = listConfigJSON
    }

    if(listViewConfig) {
      const listViewConfigJSON = new ListViewConfigurationFactory().toJSON(listViewConfig)
      requestBody.crm_listview_config = listViewConfigJSON
    }

    headers.append("crm-listview-api-name", `${listViewApiName}`);


    try {

      dispatch(purgeStore());
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.listview){
          dispatch(storeError("Error when fetching listview"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.listviews || !Array.isArray(data.listviews)){
          dispatch(storeError("Error when fetching listviews"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.objectpages || !data.objectpages[0]){
          dispatch(storeError("Error when fetching objectpage"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.list_config){
          dispatch(storeError("Error when fetching list_config"));
          dispatch(storeFetching(false));
          return;
      }

      if(data.listview.user_preference?.DisplayType_sys === 'BarChart'){
        //displaytype BarChart requires barchart dictionary
        if(!data.barchart){
            dispatch(storeError("Error when fetching BarChart"));
            dispatch(storeFetching(false));
            return;
        }
      } else {
        //display type "List" always requires
        //records, metadata, paginator
        if(!data.records || !Array.isArray(data.records)){
            dispatch(storeError("Error when fetching records"));
            dispatch(storeFetching(false));
            return;
        }
        if(!data.metadata || !data.metadata.object){
            dispatch(storeError("Error when fetching data"));
            dispatch(storeFetching(false));
            return;
        }

        if(!data.listview_config){
            dispatch(storeError("Error when fetching listview_config"));
            dispatch(storeFetching(false));
            return;
        }
        //store object metadata
        if(refetch === false){
          dispatch(
            storeObjectMetadata(new ObjectFactory().fromJSON(data.metadata.object))
          )
        }
      }

      //save listviews
      dispatch(
        storeListViews(data.listviews.map((listview: any) => new ListViewFactory().fromJSON(listview)))
      )

      //store selected list view
      const thisSelectedListView = new ListViewFactory().fromJSON(data.listview)
      dispatch(storeSelectedListView(thisSelectedListView))

      const thisListConfig = new ListConfigurationFactory().fromJSON(data.list_config);
      dispatch(storeListConfig(thisListConfig))

      if(data.listview.user_preference?.DisplayType_sys === 'BarChart'){
        dispatch(
          storeChart(new PlotlyChartFigure().fromJSON(data.barchart))
        )
//        dispatch(
//          storeListViewConfig(new ListViewConfigurationFactory().fromJSON(data.listview_config))
//        )

      } else {
        //store records
        dispatch(
          storeRecords(data.records.map((record: any) => new RecordFactory().fromJSON(record)))
        )
        //store listview config
        const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config)
        dispatch(storeListViewConfig(thisListViewConfig))
        //prefetch more records (next page)
        dispatch(prefetchRecords(
          objectApiName,
          thisSelectedListView.ApiName_sys,
          thisListConfig,
          thisListViewConfig,
          accessToken,
          handleInvalidToken
        ))
      }

      //store object page (for now, only one object page is allowed)
      dispatch(
        storeObjectPage(new ObjectPageFactory().fromJSON(data.objectpages[0]))
      )

      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };


export const switchPaginatorPage =
  (objectApiName: string | null,
   listViewApiName: string | undefined,
   listConfig: ListConfigurationModel,
   listViewConfig: ListViewConfigurationModel,
   newPage: number,
   accessToken: string,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //set the new paginator when switching a page
    const pageCount = Math.ceil(listViewConfig.paginator.recordCount/listViewConfig.paginator.pageSize)
    const nextPage = Math.min(newPage + 1, pageCount-1); //use pageCount-1 because pageNumber is actually the index
    const previousPage = Math.max(0,newPage - 1);

    const newListViewConfig = {...listViewConfig,
                               paginator: {...listViewConfig.paginator,
                                           currentPage: newPage,
                                           previousPage: previousPage,
                                           nextPage: nextPage}}

    //update the paginator
    dispatch(storeListViewConfig(newListViewConfig));

    //and prefetch records
    dispatch(prefetchRecords(
          objectApiName,
          listViewApiName,
          listConfig,
          newListViewConfig,
          accessToken,
          handleInvalidToken
    ))

  }


export const retrieveSortedRecords =
  (objectApiName: string | null,
   listViewApiName: string | undefined,
   listConfig: ListConfigurationModel,
   listViewConfig: ListViewConfigurationModel,
   sortOrders: SortOrderModel[],
   accessToken: string,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //load listviews, selected list view, records and object metadata
    //for a given objectApiName
    //if refetch is true, object metadata state is not being altered

    if(!listViewApiName){
      dispatch(storeError("Unhandled error: ListView not defined"));
      dispatch(storeFetching(false));
      return;
    }

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;
    const headers = new Headers();

    const listConfigJSON = new ListConfigurationFactory().toJSON(listConfig)
    const listViewConfigJSON = new ListViewConfigurationFactory().toJSON(listViewConfig)

    const sortOrderFactory = new SortOrderFactory();
    const sortOrdersJSON = sortOrders.map((thisSortOrder: SortOrderModel) => sortOrderFactory.toJSON(thisSortOrder));


    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    headers.append("crm-listview-api-name", `${listViewApiName}`);
//    headers.append("crm-list-config", JSON.stringify(listConfigJSON));
//    headers.append("crm-listview-config", JSON.stringify(listViewConfigJSON));
    headers.append("crm-sort-orders", JSON.stringify(sortOrdersJSON));

    const requestBody: RequestBody = {
      crm_barchart_config: null,
      crm_list_config: listConfigJSON,
      crm_listview_config: listViewConfigJSON
    };

    try {

      dispatch(storeFetching(true));
      dispatch(purgePaginator());

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.records || !Array.isArray(data.records)){
          dispatch(storeError("Error when fetching records"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.listview_config){
          dispatch(storeError("Error when fetching listview_config"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.listview){
          dispatch(storeError("Error when fetching listview"));
          dispatch(storeFetching(false));
          return;
      }

      //store records
      dispatch(
        restoreRecords(data.records.map((record: any) => new RecordFactory().fromJSON(record)))
      )

      //store list view config
      const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config)
      dispatch(storeListViewConfig(thisListViewConfig))

      //store selected list view
      const thisSelectedListView = new ListViewFactory().fromJSON(data.listview);
      dispatch(storeSelectedListView(thisSelectedListView))

      //prefetch records
      dispatch(prefetchRecords(
        objectApiName,
        thisSelectedListView.ApiName_sys,
        listConfig,
        thisListViewConfig,
        accessToken,
        handleInvalidToken
      ))

      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };


export const retrieveFilteredRecords =
  (objectApiName: string | null,
   listViewApiName: string | undefined,
   listConfig: ListConfigurationModel,
   accessToken: string,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //load listviews, selected list view, records and object metadata
    //for a given objectApiName


    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;
    const headers = new Headers();

    const listConfigJSON = new ListConfigurationFactory().toJSON(listConfig);

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    const requestBody: RequestBody = {
      crm_barchart_config: null,
      crm_list_config: listConfigJSON,
      crm_listview_config: null
    };

    try {

      dispatch(storeFetching(true));
      dispatch(purgePaginator());

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }


      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.list_config){
          dispatch(storeError("Error when fetching list_config"));
          dispatch(storeFetching(false));
          return;
      }

      const thisListConfig = new ListConfigurationFactory().fromJSON(data.list_config);
      dispatch(storeListConfig(thisListConfig))

      if(data.listview.user_preference?.DisplayType_sys === 'BarChart'){
        //displaytype BarChart requires barchart dictionary
        if(!data.barchart){
            dispatch(storeError("Error when fetching BarChart"));
            dispatch(storeFetching(false));
            return;
        }
      } else {
        //display type "List" always requires
        //records, metadata, paginator
        if(!data.records || !Array.isArray(data.records)){
            dispatch(storeError("Error when fetching records"));
            dispatch(storeFetching(false));
            return;
        }

        if(!data.listview_config){
            dispatch(storeError("Error when fetching listview_config"));
            dispatch(storeFetching(false));
            return;
        }
      }

      if(data.listview.user_preference?.DisplayType_sys === 'BarChart'){
        dispatch(
          storeChart(new PlotlyChartFigure().fromJSON(data.barchart))
        )
//        dispatch(
//          storeListViewConfig(new ListViewConfigurationFactory().fromJSON(data.listview_config))
//        )
      } else {
        //store records
        dispatch(
          restoreRecords(data.records.map((record: any) => new RecordFactory().fromJSON(record)))
        )
        //store listview configuration
        const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config);
        dispatch(storeListViewConfig(thisListViewConfig))
        //prefetch records
        dispatch(prefetchRecords(
          objectApiName,
          listViewApiName,
          thisListConfig,
          thisListViewConfig,
          accessToken,
          handleInvalidToken
        ))
      }

      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };


export const prefetchRecords =
  (objectApiName: string | null,
   listViewApiName: string | undefined,
   listConfig: ListConfigurationModel,
   listViewConfig: ListViewConfigurationModel,
   accessToken: string,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //for a given listViewConfig, load the NEXT page, not the current one

    const thisPaginator = listViewConfig.paginator;
    const pageNumber = thisPaginator.nextPage;

    if(thisPaginator.currentPage === pageNumber){
      //nothing to be done, because end of list already found
      return;
    }
    if(thisPaginator.loadedPages.includes(pageNumber)){
      //nothing to be done, because page already loaded
      return;
    }
    if(thisPaginator.loadingPages.includes(pageNumber)){
      //nothing to be done, because page is currently already being loaded
      return;
    }
    if(!listViewApiName){
      //if there is no listview, nothing to be loaded
      return;
    }

    //now, set paginator.currentPage to the nextPage
    //and load stuff

    const newListViewConfig = {...listViewConfig,
                               paginator: {...listViewConfig.paginator,
                                           currentPage: pageNumber}}


    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;
    const headers = new Headers();

    const thisListConfigJSON = new ListConfigurationFactory().toJSON(listConfig)
    const thisListViewConfigJSON = new ListViewConfigurationFactory().toJSON(newListViewConfig)

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    headers.append("crm-listview-api-name", `${listViewApiName}`);

    const requestBody: RequestBody = {
      crm_barchart_config: null,
      crm_list_config: thisListConfigJSON,
      crm_listview_config: thisListViewConfigJSON
    };

    try {
      dispatch(storePageLoading(pageNumber));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error in prefetch:', errorData)
        return;
      }

      const data = await response.json();

      if(!data) return;
      if(!data.records || !Array.isArray(data.records)) return;
      if(!data.listview_config) return

      //store records
      dispatch(
        storeRecords(data.records.map((record: any) => new RecordFactory().fromJSON(record)))
      )

      //store actually loaded page from listview_config
      const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config);
      const loadedPageNumber = thisListViewConfig.paginator.currentPage
      dispatch(storePageLoaded(loadedPageNumber));

    } catch (error) {
      dispatch(removePageLoading(pageNumber))
      console.error(error);
    }
  };


export const selectIsFetching = (state: RootState) =>
  state.objectStore.isFetching;

export const selectError = (state: RootState) =>
  state.objectStore.error;

export const selectObjectMetadata = (state: RootState) =>{
    return state.objectStore.objectMetadata;
}

export const selectSelectedListView = (state: RootState) =>{
    return state.objectStore.selectedListView;
}

export const selectObjectPage = (state: RootState) =>{
    return state.objectStore.objectPage;
}

export const selectActions = (state: RootState) =>{
  if(state.objectStore.objectPage === null) return []
  return state.objectStore
              .objectPage
              .objectpage_buttons
              .map((thisButton) => thisButton.action)
              .filter((thisAction) => thisAction.OnSuccess_sys.type === 'dialog' || thisAction.OnSuccess_sys.type === 'redirect')
}


export const selectSelectedListViewColumns = (state: RootState) =>{
  if(state.objectStore.selectedListView === null){
    return []
  } else
  {
    return state.objectStore.selectedListView.listview_columns.map((column) => column);
  }
}

export const selectListViews = (state: RootState) =>{
    return state.objectStore.listViews;
}

export const selectRecords = (state: RootState) =>{
    return state.objectStore.records;
}

export const selectListConfig = (state: RootState) =>{
    return state.objectStore.listConfig;
}

export const selectListViewConfig = (state: RootState) =>{
    return state.objectStore.listViewConfig;
}

/*
export const selectPaginator = (state: RootState) =>{
    return state.objectStore.listViewConfig.paginator;
}

export const selectSearchTerm = (state: RootState) =>{
  return state.objectStore.listViewConfig.searchTerm;
}
*/

export const selectChart = (state: RootState) =>{
    return state.objectStore.chart;
}

export default objectSlice.reducer;
